import { ThemeEvents } from '@theme/events';

/** Hoe lang "Toegevoegd" naast de knop blijft staan. */
const ADDED_VISIBLE_DURATION = 2000;

/** Speling in px voor "helemaal links" / "helemaal rechts" — subpixels en snap-offsets. */
const EDGE_TOLERANCE = 4;

/**
 * Carousel-navigatie, "Toegevoegd"-bevestiging en klik-tracking voor het intent block.
 *
 * Toevoegen aan de winkelwagen doet het blok zelf niet: elke kaart draagt een eigen
 * <product-form-component> (hetzelfde mechanisme als de koopknop en quick-add), dus de
 * cart drawer, het aantal in de header en de foutafhandeling komen van het thema.
 *
 * Alle listeners hangen aan dit element en worden in disconnectedCallback afgebroken,
 * dus een sectie-rerender bij een variantwissel kan geen dubbele listeners opleveren.
 */
class IntentBlockComponent extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {number[]} */
  #timeouts = [];

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {HTMLElement | undefined} */
  #track;
  /** @type {HTMLButtonElement | undefined} */
  #prev;
  /** @type {HTMLButtonElement | undefined} */
  #next;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    const track = this.querySelector('[data-intent-track]');
    const prev = this.querySelector('[data-intent-prev]');
    const next = this.querySelector('[data-intent-next]');

    if (!(track instanceof HTMLElement) || !(prev instanceof HTMLButtonElement) || !(next instanceof HTMLButtonElement)) {
      return;
    }

    this.#track = track;
    this.#prev = prev;
    this.#next = next;

    prev.addEventListener('click', () => track.scrollBy({ left: -this.#step() }), { signal });
    next.addEventListener('click', () => track.scrollBy({ left: this.#step() }), { signal });
    track.addEventListener('scroll', this.#updateNav, { passive: true, signal });

    // Een enkele meting vlak na connectedCallback is te vroeg: de track heeft dan nog
    // niet zijn uiteindelijke breedte. De observer vuurt ook meteen bij observe().
    // Ook de eerste kaart wordt gevolgd: de track zelf is altijd even breed als de
    // kolom, dus als die eerste meting misgaat is er geen tweede — de kaart krijgt
    // zijn breedte wel later, zodra de stylesheet van het blok is toegepast.
    this.#resizeObserver = new ResizeObserver(this.#updateNav);
    this.#resizeObserver.observe(track);
    const firstCard = track.querySelector('.intent-card');
    if (firstCard) this.#resizeObserver.observe(firstCard);

    if (document.readyState !== 'complete') {
      window.addEventListener('load', this.#updateNav, { signal });
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    this.#timeouts.forEach((id) => clearTimeout(id));
    this.#timeouts = [];
  }

  /** Eén kaart plus de gap — de breedte van één scrollstap. */
  #step() {
    const card = this.#track?.querySelector('.intent-card');
    if (!(card instanceof HTMLElement) || !this.#track) return 0;

    const gap = parseFloat(getComputedStyle(this.#track).columnGap) || 0;
    return card.offsetWidth + gap;
  }

  #updateNav = () => {
    const track = this.#track;
    const prev = this.#prev;
    const next = this.#next;
    if (!track || !prev || !next) return;

    const scrollable = track.scrollWidth > track.clientWidth + 2;
    prev.hidden = !scrollable;
    next.hidden = !scrollable;
    if (!scrollable) return;

    prev.disabled = track.scrollLeft <= EDGE_TOLERANCE;
    next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - EDGE_TOLERANCE;
  };

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const card = target.closest('[data-intent-to]');
    if (!(card instanceof HTMLElement)) return;

    const actionElement = target.closest('[data-intent-action]');
    const action = actionElement instanceof HTMLElement ? actionElement.dataset.intentAction : 'link';

    try {
      window.Shopify?.analytics?.publish('intent_block_click', {
        from: this.dataset.intentFrom,
        to: card.dataset.intentTo,
        action,
      });
    } catch (error) {
      // tracking mag een klik nooit in de weg zitten
    }
  };

  /** @param {Event} event */
  #onCartUpdate = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const card = target.closest('.intent-card');
    if (!(card instanceof HTMLElement) || !card.querySelector('.intent-card__added')) return;

    // Bij een fout meldt het thema dat zelf via de addToCartTextError-ref.
    if (/** @type {CustomEvent} */ (event).detail?.data?.didError) return;

    card.classList.add('intent-card--added');
    const timeout = setTimeout(() => {
      card.classList.remove('intent-card--added');
      this.#timeouts = this.#timeouts.filter((id) => id !== timeout);
    }, ADDED_VISIBLE_DURATION);

    this.#timeouts.push(timeout);
  };
}

if (!customElements.get('intent-block-component')) {
  customElements.define('intent-block-component', IntentBlockComponent);
}
